import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import withErrorHandling from "../HOC/ErrorHandling";
import { useSharedProps } from "../HOC/SharedProps";

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  MenuItem,
  Snackbar,
  Alert,
  TextField,
  Typography,
} from "@mui/material";

import { Delete as DeleteIcon } from "@mui/icons-material";

import { styled } from "@mui/material/styles";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  Lock as LockIcon,
  Info as InfoIcon,
} from "@mui/icons-material";

import { CopyBlock, dracula } from "react-code-blocks";

import t from "../helpers/en";
import { JSON_ITEM, MAX_LIMITS } from "../helpers/constants";
import {
  restartService,
  getServiceInstanceLogs,
  forceRebuild,
  deleteApplication,
  renameApplication,
  getPlatformDomains,
  getDomainAssignments,
  assignDomainApi,
  unassignDomainApi,
  getImages,
} from "../helpers/utils";
import { useBindingGraph } from "../HOC/BindingGraphProvider";
import { hasPermission } from "../helpers/selectors";
import {
  canAddApplications,
  addApplicationsHelperText,
} from "../helpers/appPermissions";
// SCK-547: PermissionsProvider hook available for reactive gates that
// don't need the appPermissions.js project-scoped 3-part check.
import { useHasPermission } from "../contexts/PermissionsProvider";

import Modal from "../components/Modal/Modal";
import { AddAppWizard } from "../components/AddAppWizard";
import Loading from "../components/Loading";
import MetricsPanel from "../components/MetricsPanel";
import AppFlowModal from "../components/AppFlowModal/AppFlowModal";
import { UnifiedDashboard } from "../components/UnifiedDashboard";
// SCK-705: post-NDA beta welcome modal. Self-gates via localStorage
// keyed by JWT sub — ManageApps just mounts it at the landing page.
import WelcomeModal from "../components/WelcomeModal";

import { useUnifiedApps } from "../hooks/useUnifiedApps";
import { useViewPreference } from "../hooks/useViewPreference";
import { useCurrentAccountMember } from "../hooks/useCurrentAccountMember";

import {
  styleButton,
  freeTierStyles,
} from "../GeneralStyles";

const ADD = "ADD";
const ADD_FROM_JSON = "ADD_FROM_JSON";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const ManageApps = (props) => {
  const { showError, showSuccess, currentUser } = props;

  // SCK-547: subscribe to the live permissions snapshot so the
  // Add/Upload/Add-Service buttons and the "ask an admin" fallback
  // rerender when an operator toggles the caller's role — no more
  // sign-out/sign-in loop required. Falls back to the JWT-decode
  // path automatically if the /api/me/permissions endpoint isn't
  // deployed yet (see PermissionsProvider).
  const hasAddApplicationsGrant = useHasPermission(["addApplications"]);
  const canCreateServiceInstance = useHasPermission(["CreateServiceInstance"]);

  // Use the unified apps hook
  const { apps, loading, refresh } = useUnifiedApps({
    showError,
  });

  // View mode preference
  const [viewMode, setViewMode] = useViewPreference();

  // Bindings for chip strips come from the project-scoped composite
  // graph (BindingGraphProvider → coord PR #231). One fetch per
  // project, O(1) lookups per card — no per-app fan-out on the
  // useUnifiedApps poll tick. Mutations are invalidated inside the
  // AppFlowModal Bindings tab; ManageApps just consumes the cache.
  const { bindingsForApp: graphBindingsForApp } = useBindingGraph();

  // Modal states
  const [form, setForm] = useState({ open: false, fileKey: 0 });
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // App Flow Modal
  const [appFlowModalOpen, setAppFlowModalOpen] = useState(false);
  const [selectedAppForFlow, setSelectedAppForFlow] = useState(null);
  const [appFlowInitialTab, setAppFlowInitialTab] = useState(0);

  // Metrics Modal
  const [selectedItem, setSelectedItem] = useState(null);
  const [isMetricsOpen, setIsMetricsOpen] = useState(false);

  // Rename Dialog
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameApp, setRenameApp] = useState(null);
  const [newAppName, setNewAppName] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  // Delete Confirmation Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteApp, setDeleteApp] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Domain Management Dialog
  const [domainsDialogOpen, setDomainsDialogOpen] = useState(false);
  const [domainsApp, setDomainsApp] = useState(null);
  const [domainAssignments, setDomainAssignments] = useState([]);
  const [availableDomains, setAvailableDomains] = useState([]);
  const [domainForm, setDomainForm] = useState({ subdomain: "", selectedDomain: "", appName: "" });
  const [isAssigning, setIsAssigning] = useState(false);

  const { currentGroup } = useSharedProps();
  const navigate = useNavigate();
  const location = useLocation();

  // Coord-side member row for the caller (role, canCreateApplications,
  // per-project grants). Feeds the canAddApplications gate below.
  // Falsy on 404/403 or older coord builds without the field — the
  // gate collapses to "hidden" per the safe-default contract in
  // helpers/appPermissions.js.
  const { accountMember } = useCurrentAccountMember();
  const gateInput = {
    currentUser,
    accountMember,
    projectId: currentGroup?.projectId,
  };
  const canAddApp = canAddApplications(gateInput);
  const addAppHelperText = addApplicationsHelperText(gateInput);

  // Open add application modal
  const onOpen = async (evt, row) => {
    const { id } = evt.target;

    setForm((prev) => ({
      ...prev,
      pressedBtn: id,
      isLoading: true,
      open: true,
    }));

    const promisify = (fn, params) => {
      return new Promise((resolve, reject) => {
        fn(params, (result, err) => {
          if (err) reject(err);
          else resolve(result);
        });
      });
    };

    try {
      let modalData = {};

      if (id === JSON_ITEM) {
        const result = await promisify(getServiceInstanceLogs, {
          name: row?.service?.metadata?.name,
        });
        modalData.jsonData = result;
      } else {
        modalData = { ...(row ? { row } : {}) };
      }

      setForm((prev) => ({
        ...prev,
        ...modalData,
        isLoading: false,
      }));
    } catch (err) {
      showError(err);
      setForm((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const onExportData = () => {
    const blob = new Blob([JSON.stringify(form, null, 2)], {
      type: "application/json",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "";
    link.click();
  };

  // Success-ceremony hook: capture the set of app IDs visible BEFORE
  // the wizard opens. When the wizard submits and refresh() returns,
  // anything new is the celebrating app — auto-open AppFlowModal on
  // it so the user watches their build animate to green. Also flash
  // a celebratory snackbar.
  const knownAppIdsBeforeWizard = useRef(new Set());
  const [shouldCelebrateNext, setShouldCelebrateNext] = useState(false);
  const [celebrationSnack, setCelebrationSnack] = useState({ open: false, name: "" });

  const triggerUpdateTable = () => {
    // Snapshot the existing app IDs so the post-refresh effect can
    // tell which one is new.
    knownAppIdsBeforeWizard.current = new Set((apps || []).map((a) => a.id));
    setShouldCelebrateNext(true);
    refresh();
    onClose();
  };

  // Fan out one call per app to fetch its bound Daari services. Runs
  // Derive the per-app chip map from the cached graph. Memoised on the
  // stable set of app names (not the `apps` array identity, which
  // flips on every 3s poll) so re-renders don't churn the map object.
  const appNamesKey = Array.isArray(apps)
    ? apps.map((a) => a?.name).filter(Boolean).sort().join(",")
    : "";
  const boundServicesByApp = useMemo(() => {
    const map = {};
    if (!Array.isArray(apps)) return map;
    for (const app of apps) {
      const name = app?.name;
      if (!name) continue;
      const bindings = graphBindingsForApp(name);
      if (bindings.length > 0) map[name] = bindings;
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appNamesKey, graphBindingsForApp]);

  // Chip click deep-links straight to the per-instance detail page
  // (/daari/:path/:name) shipped in PR-9.
  const handleOpenBoundService = useCallback(
    (svc) => {
      if (!svc?.k8sName || !svc?.kind) {
        navigate("/daari");
        return;
      }
      const path = svc.kind === "llm_gateway" ? "llm-gateway" : svc.kind;
      navigate(`/daari/${encodeURIComponent(path)}/${encodeURIComponent(svc.k8sName)}`);
    },
    [navigate]
  );

  // Watch apps for the freshly-created entry and pop the pipeline modal
  // on it — the success ceremony is the user watching their own build go.
  useEffect(() => {
    if (!shouldCelebrateNext) return;
    if (!Array.isArray(apps) || apps.length === 0) return;
    const newOne = apps.find((a) => a.id && !knownAppIdsBeforeWizard.current.has(a.id));
    if (!newOne) return;
    setShouldCelebrateNext(false);
    setCelebrationSnack({ open: true, name: newOne.displayName || newOne.name });
    // Slight delay so the snackbar registers before the modal opens.
    setTimeout(() => handleCardClick(newOne), 250);
  }, [apps, shouldCelebrateNext]);

  // Deep-link is wired below handleCardClick to satisfy no-use-before-define.
  const handledOpenAppRef = useRef(null);

  const onClose = (shouldDiscard) => {
    if (shouldDiscard && hasChanges) {
      setIsDiscardOpen(true);
    } else {
      setForm({ open: false, fileKey: 0 });
      setIsDiscardOpen(false);
      setHasChanges(false);
    }
  };

  const onFileUpload = (evt) => {
    const file = evt.target.files[0];

    if (!file) return;

    if (file.type !== "application/json") {
      showError("Only JSON files are allowed");
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const body = JSON.parse(e.target.result);
        setForm((prev) => ({
          ...prev,
          ...(body ?? {}),
          isSetJson: true,
          isLoading: false,
          pressedBtn: ADD_FROM_JSON,
          open: true,
          fileKey: prev.fileKey + 1,
        }));
        setHasChanges(true);
      } catch (error) {
        showError("Invalid JSON file");
        setForm((prev) => ({
          ...prev,
          isLoading: false,
          fileKey: prev.fileKey + 1,
        }));
      }
    };

    reader.readAsText(file);
  };

  const onDiscardClose = (shouldDiscard) => {
    if (shouldDiscard) {
      onClose();
    } else {
      setIsDiscardOpen(false);
    }
  };

  const onDetectFormChange = () => {
    setHasChanges(true);
  };

  // Handle card click - open pipeline modal
  const handleCardClick = useCallback((app) => {
    // Check if app is currently building
    const isBuilding = app.overallStatus === "building";

    // Pass full app data including runtime info
    setSelectedAppForFlow(app);
    setAppFlowInitialTab(isBuilding ? 1 : 0); // 1 = Build Logs tab
    setAppFlowModalOpen(true);
  }, []);

  // Deep-link: /manage-apps?openApp=<uid | name | displayName> opens the
  // pipeline modal on that app once the list has resolved. Used by the
  // Daari bound-apps chip strip to jump from a service to its consumer.
  // The query param is consumed once — we strip it after handling so a
  // navigation refresh or back-nav doesn't re-open the modal.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const target = params.get("openApp");
    if (!target) return;
    if (!Array.isArray(apps) || apps.length === 0) return;
    if (handledOpenAppRef.current === target) return;
    const match = apps.find(
      (a) => a?.id === target || a?.name === target || a?.displayName === target
    );
    if (!match) return;
    handledOpenAppRef.current = target;
    handleCardClick(match);
    // Strip the query param so a refresh doesn't loop.
    params.delete("openApp");
    const nextSearch = params.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" },
      { replace: true }
    );
  }, [apps, location.search, location.pathname, navigate, handleCardClick]);

  const handleCloseAppFlowModal = () => {
    setAppFlowModalOpen(false);
    setSelectedAppForFlow(null);
  };

  // Handle restart
  const handleRestart = useCallback((app) => {
    const name = app.name;
    restartService(name, (result, err) => {
      if (err) {
        showError(err);
      } else {
        refresh();
      }
    });
  }, [showError, refresh]);

  // Handle rebuild
  const handleRebuild = useCallback((app) => {
    const name = app.name;
    forceRebuild({ name }, (result, err) => {
      if (err) {
        showError(err);
      } else {
        refresh();
      }
    });
  }, [showError, refresh]);

  // Handle rename - open dialog
  const handleRenameClick = useCallback((app) => {
    setRenameApp(app);
    setNewAppName(app.name || "");
    setRenameDialogOpen(true);
  }, []);

  // Confirm rename
  const handleRenameConfirm = useCallback(() => {
    if (!renameApp || !newAppName.trim()) return;

    const oldName = renameApp.name;
    const trimmedNewName = newAppName.trim().toLowerCase().replace(/\s+/g, '-');

    if (oldName === trimmedNewName) {
      setRenameDialogOpen(false);
      return;
    }

    setIsRenaming(true);
    renameApplication(oldName, trimmedNewName, (result, err) => {
      setIsRenaming(false);
      if (err) {
        showError(err);
      } else {
        setRenameDialogOpen(false);
        setRenameApp(null);
        setNewAppName("");
        refresh();
      }
    });
  }, [renameApp, newAppName, showError, refresh]);

  // Handle delete - open dialog
  const handleDeleteClick = useCallback((app) => {
    setDeleteApp(app);
    setDeleteDialogOpen(true);
  }, []);

  // Confirm delete. The kpack-image UID is the canonical identifier
  // expected by DELETE /k8s/app/{uid}. Some app objects passed in from
  // AppFlowModal lack metadata.uid (e.g. BuildFailed apps where the
  // merge picked a runtime-only object), so fall back to re-fetching
  // the images list and resolving by name before giving up.
  const handleDeleteConfirm = useCallback(() => {
    if (!deleteApp) return;

    const finishDelete = (uid) => {
      setIsDeleting(true);
      deleteApplication(uid, (result, err) => {
        setIsDeleting(false);
        if (err) {
          showError(err);
        } else {
          setDeleteDialogOpen(false);
          setDeleteApp(null);
          refresh();
        }
      });
    };

    const directUid = deleteApp._raw?.build?.metadata?.uid || deleteApp.id;
    if (directUid) {
      finishDelete(directUid);
      return;
    }

    if (!deleteApp.name) {
      // eslint-disable-next-line no-console
      console.error("delete: no uid AND no name on app object", deleteApp);
      showError("Unable to delete: missing app identifier");
      return;
    }

    // Fallback: refetch images and look up by kpack-image name.
    setIsDeleting(true);
    getImages(null, (images, err) => {
      if (err || !Array.isArray(images)) {
        setIsDeleting(false);
        // eslint-disable-next-line no-console
        console.error("delete: image lookup failed", err, deleteApp);
        showError("Unable to delete: failed to look up app identifier");
        return;
      }
      const match = images.find((img) => img?.metadata?.name === deleteApp.name);
      const resolvedUid = match?.metadata?.uid;
      if (!resolvedUid) {
        setIsDeleting(false);
        // eslint-disable-next-line no-console
        console.error("delete: no matching image by name", deleteApp.name, deleteApp);
        showError("Unable to delete: app not found in current image list");
        return;
      }
      finishDelete(resolvedUid);
    });
  }, [deleteApp, showError, refresh]);

  const onCloseMetricsModal = () => {
    setIsMetricsOpen(false);
    setSelectedItem(null);
  };

  // Handle manage domains - open dialog
  const handleManageDomains = useCallback((app) => {
    const appName = app.name;
    setDomainsApp(app);
    setDomainForm({ subdomain: "", selectedDomain: "", appName });
    setDomainsDialogOpen(true);

    // Fetch platform domains
    getPlatformDomains((result) => {
      if (result) setAvailableDomains(result);
    });

    // Fetch assignments for this app's project
    const projectId = currentGroup?.projectId;
    if (projectId) {
      getDomainAssignments(projectId, (result) => {
        if (result) {
          // Filter to just this app's assignments
          setDomainAssignments(result.filter((a) => a.appName === appName));
        }
      });
    }
  }, [currentGroup]);

  const handleAssignDomain = useCallback(() => {
    if (!domainForm.subdomain || !domainForm.selectedDomain) return;
    setIsAssigning(true);

    assignDomainApi(
      {
        subdomain: domainForm.subdomain,
        domain: domainForm.selectedDomain,
        appName: domainsApp?.name,
        projectId: currentGroup?.projectId,
      },
      (result, err) => {
        setIsAssigning(false);
        if (result) {
          setDomainForm((prev) => ({ ...prev, subdomain: "" }));
          // Refresh assignments
          handleManageDomains(domainsApp);
        } else {
          showError(err);
        }
      }
    );
  }, [domainForm, domainsApp, currentGroup, showError, handleManageDomains]);

  const handleUnassignDomain = useCallback((assignmentId) => {
    unassignDomainApi(assignmentId, (result, err) => {
      if (!err) {
        setDomainAssignments((prev) => prev.filter((a) => a.id !== assignmentId));
      } else {
        showError(err);
      }
    });
  }, [showError]);

  // Modal content
  let title, content, saveLabel, onSave;

  switch (form?.pressedBtn) {
    case JSON_ITEM:
      title = t.instanceLogs;
      content = form?.isLoading ? (
        <Loading />
      ) : (
        <Box>
          <CopyBlock
            customStyle={{ height: "550px", width: "100%", overflow: "scroll" }}
            text={form?.jsonData}
            language={"en"}
            showLineNumbers={true}
            theme={dracula}
          />
        </Box>
      );
      saveLabel = "Download";
      onSave = () => onExportData();
      break;
    case ADD:
      title = t.add(t.application);
      content = (
        <AddAppWizard
          handleClose={() => onClose(true)}
          {...{ triggerUpdateTable, currentUser, onDetectFormChange, apps }}
        />
      );
      break;
    case ADD_FROM_JSON:
      title = t.add(t.application);
      content = form?.isLoading ? (
        <Loading />
      ) : (
        <AddAppWizard
          data={form}
          handleClose={() => onClose(true)}
          {...{ triggerUpdateTable, currentUser, onDetectFormChange, apps }}
        />
      );
      break;
    default:
  }

  const limitExceeded =
    !currentUser?.account?.billingEnabled &&
    apps?.length >= MAX_LIMITS.applications;

  return (
    <Box>
      {/* SCK-705: post-NDA welcome + beta guide modal. Renders once
          per user (localStorage keyed by JWT sub); no-op for SYSTEM
          operators. Mounted at the top of the landing tree so it
          only fires when the user first lands on ManageApps. */}
      <WelcomeModal currentUser={currentUser} hasMembership />

      {/* Success ceremony — fires when wizard submit creates a new app.
          Brief celebratory snackbar; the AppFlowModal also auto-opens
          on the new app so the user watches the build run live. */}
      <Snackbar
        open={celebrationSnack.open}
        autoHideDuration={6000}
        onClose={() => setCelebrationSnack({ open: false, name: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          icon={<span style={{ fontSize: "1.1rem" }}>🚀</span>}
          sx={{
            background: "linear-gradient(135deg, #EA580C 0%, #F97316 100%)",
            color: "#fff",
            fontWeight: 500,
            boxShadow: "0 8px 24px rgba(234,88,12,0.35)",
            "& .MuiAlert-icon": { color: "#fff" },
          }}
          onClose={() => setCelebrationSnack({ open: false, name: "" })}
        >
          Build started for <strong>{celebrationSnack.name}</strong> — should be live in ~3 min.
        </Alert>
      </Snackbar>

      {/* Add Application Modal */}
      <Modal
        open={form?.open}
        {...{ title, content, saveLabel, onSave }}
        closeLabel={t.close}
        handleClose={() =>
          onClose(
            form?.pressedBtn === ADD || form?.pressedBtn === ADD_FROM_JSON
          )
        }
        specificDetails={
          (form?.pressedBtn === ADD || form?.pressedBtn === ADD_FROM_JSON) && {
            shouldHideFooter: true,
            style: {
              contentModal: {
                height: { xs: "80vh", md: "75vh", lg: "80vh" },
                padding: 0,
              },
            },
          }
        }
      />

      {/* Discard Modal */}
      <Modal
        title={t.discardChangesTitle}
        open={isDiscardOpen}
        content={t.discardChangesMsg}
        saveLabel={t.confirmDiscard}
        closeLabel={t.closeDiscard}
        handleClose={() => onDiscardClose(false)}
        onSave={() => onDiscardClose(true)}
        specificDetails={{ style: { dialog: { maxWidth: "35em" } } }}
      />

      {/* Metrics Modal */}
      <Modal
        open={isMetricsOpen}
        title={`"${selectedItem?.service?.metadata?.name || selectedItem?.name}" application metrics`}
        content={
          <MetricsPanel {...props} service={selectedItem?.service || selectedItem} />
        }
        closeLabel={t.close}
        handleClose={onCloseMetricsModal}
      />

      {/* App Flow Modal */}
      <AppFlowModal
        open={appFlowModalOpen}
        onClose={handleCloseAppFlowModal}
        appData={selectedAppForFlow}
        onRefresh={refresh}
        showError={showError}
        showSuccess={showSuccess}
        currentUser={currentUser}
        onRename={(app) => {
          // Convert appData format to the format expected by handleRenameClick
          const appForRename = {
            name: app?.metadata?.name || app?.name,
            displayName: app?.metadata?.displayName || app?.displayName,
            _raw: { build: app },
          };
          handleRenameClick(appForRename);
        }}
        onDelete={(app) => {
          // Convert appData format to the format expected by handleDeleteClick
          const appForDelete = {
            name: app?.metadata?.name || app?.name,
            displayName: app?.metadata?.displayName || app?.displayName,
            id: app?.metadata?.uid,
            _raw: { build: app },
          };
          handleDeleteClick(appForDelete);
        }}
        initialTab={appFlowInitialTab}
      />

      {/* Rename Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={() => !isRenaming && setRenameDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px" }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Rename Application</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will create a new application with the new name and delete the old one.
            All settings will be preserved.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="New Name"
            value={newAppName}
            onChange={(e) => setNewAppName(e.target.value)}
            disabled={isRenaming}
            helperText="Lowercase letters, numbers, and hyphens only"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRenameDialogOpen(false)}
            disabled={isRenaming}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRenameConfirm}
            disabled={isRenaming || !newAppName.trim()}
            sx={{
              background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
              },
            }}
          >
            {isRenaming ? "Renaming..." : "Rename"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: "16px" }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Delete Application</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteApp?.displayName || deleteApp?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            This action cannot be undone. The application and all its builds will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Domain Management Dialog */}
      <Dialog
        open={domainsDialogOpen}
        onClose={() => setDomainsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px" }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Domains - {domainsApp?.displayName || domainsApp?.name}
        </DialogTitle>
        <DialogContent>
          {/* Current assignments */}
          {domainAssignments.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Current Domains</Typography>
              <List dense>
                {domainAssignments.map((a) => (
                  <ListItem key={a.id} sx={{ px: 0 }}>
                    <ListItemText
                      primary={a.fqdn}
                      secondary={
                        <Chip
                          label={a.status || "PENDING"}
                          color={a.status === "ACTIVE" ? "success" : "warning"}
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" size="small" onClick={() => handleUnassignDomain(a.id)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Assign new domain */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Assign New Domain</Typography>
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <TextField
              select
              label="Domain"
              size="small"
              fullWidth
              value={domainForm.selectedDomain}
              onChange={(e) => setDomainForm((prev) => ({ ...prev, selectedDomain: e.target.value }))}
            >
              {availableDomains.map((d) => (
                <MenuItem key={d.id || d.domain} value={d.domain}>
                  {d.domain}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Subdomain"
              size="small"
              fullWidth
              placeholder={domainsApp?.name || "my-app"}
              value={domainForm.subdomain}
              onChange={(e) => setDomainForm((prev) => ({ ...prev, subdomain: e.target.value }))}
              helperText={
                domainForm.subdomain && domainForm.selectedDomain
                  ? `URL: ${domainForm.subdomain}.${domainForm.selectedDomain}`
                  : ""
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDomainsDialogOpen(false)}>
            {t.close}
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignDomain}
            disabled={isAssigning || !domainForm.subdomain || !domainForm.selectedDomain}
            sx={{
              background: "linear-gradient(135deg, #F97316 0%, #EA580C 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #EA580C 0%, #C2410C 100%)",
              },
            }}
          >
            {isAssigning ? "Assigning..." : t.assignDomain}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Free tier warning */}
      {limitExceeded && (
        <Box sx={{ mb: 2 }}>
          <sub style={freeTierStyles}>
            <InfoIcon /> {t.freeTier}
          </sub>
        </Box>
      )}

      {/* Unified Dashboard. Upload + Add Service flow into the hero
          as extraHeroActions so all three CTAs land in one aligned
          group on the right of "Your apps". */}
      <UnifiedDashboard
        apps={apps}
        loading={loading}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onRefresh={refresh}
        onAddApp={(evt) => onOpen(evt || { target: { id: ADD } }, null)}
        onOpenPipeline={handleCardClick}
        onRestart={handleRestart}
        onRebuild={handleRebuild}
        onRename={handleRenameClick}
        onDelete={handleDeleteClick}
        onManageDomains={handleManageDomains}
        onCardClick={handleCardClick}
        boundServicesByApp={boundServicesByApp}
        onOpenBoundService={handleOpenBoundService}
        showAddButton={canAddApp}
        addButtonHiddenReason={addAppHelperText}
        addButtonDisabled={
          (currentGroup?.id && !currentGroup?.isContributor2Group) ||
          limitExceeded
        }
        extraHeroActions={[
          canAddApp && {
            label: t.uploadApplication,
            icon: limitExceeded ? <LockIcon /> : <CloudUploadIcon />,
            disabled: (currentGroup?.id && !currentGroup?.isContributor2Group) || limitExceeded,
            component: "label",
            children: (
              <VisuallyHiddenInput
                type="file"
                key={form?.fileKey}
                onChange={onFileUpload}
                accept=".json"
              />
            ),
          },
          canCreateServiceInstance && {
            label: "Add Service",
            onClick: () => navigate("/service-catalog", { state: { from: location } }),
            disabled: currentGroup?.id && !currentGroup?.isContributor2Group,
          },
        ].filter(Boolean)}
      />

      {/* SCK-547 / Batch 12 UX: when every Add/Upload/Service-add CTA
          is hidden because the caller lacks the permission, give them
          a short line telling them who to ask instead of just showing
          an empty toolbar. Only fires when there are also no apps to
          show — otherwise the list itself gives them enough context.
      */}
      {!hasAddApplicationsGrant && !canAddApp && !canCreateServiceInstance && !loading && apps.length === 0 && (
        <Box
          sx={{
            mt: 4,
            p: 3,
            borderRadius: 2,
            border: "1px dashed",
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Ask an account admin to grant you the Create Applications permission.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default withErrorHandling(ManageApps);
